import { TextInput, Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconMail, IconLock, IconUser } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import type { TestAccountType } from '@prisma/client';
import { testStartingUrlAtom } from '~/atoms/atoms';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';

export const NewTestAccountForm = ({
  mutating,
  onMutate,
  onSettled,
  onSuccessSubmit,
  onCancel,
  saveButtonText,
  accountType,
}: {
  mutating: boolean;
  onMutate: () => void;
  onSettled: () => void;
  onSuccessSubmit: (accountId: string) => void;
  onCancel: () => void;
  saveButtonText?: string;
  accountType?: TestAccountType;
}) => {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();
  const testStartingUrl = useAtomValue(testStartingUrlAtom);

  const { mutateAsync: createTestAccount } = api.testAccount.create.useMutation({
    onMutate: () => {
      onMutate();
    },
    onSettled: () => {
      onSettled();
    },
    onSuccess: async (accountId) => {
      notifications.show({ message: 'Created the test account', color: 'blue' });
      await trpcUtils.testAccount.list.invalidate({ projectId });
      newAccountForm.reset();
      onSuccessSubmit(accountId);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: e.message, color: 'red' });
    },
  });

  const newAccountForm = useForm({
    initialValues: {
      name: '',
      email: '',
      password: '',
      url: testStartingUrl,
    },
    validate: {
      name: (value) => (!value ? 'Name is required' : null),
      email: (value) => (!value ? 'Email is required' : null),
      password: (value) => (!value ? 'Password is required' : null),
    },
  });

  return (
    <div>
      <form
        className="flex flex-col gap-2"
        onSubmit={newAccountForm.onSubmit(async (values) => {
          if (!projectId) {
            throw new Error('Project ID is required');
          }
          await createTestAccount({
            projectId,
            data: {
              ...values,
              userName: values.email,
              url: values.url || '',
              type: accountType,
            },
          });
        })}
      >
        <TextInput
          autoFocus
          placeholder="Email"
          leftSection={<IconMail size={14} />}
          {...newAccountForm.getInputProps('email')}
        />
        <TextInput
          placeholder="Password"
          leftSection={<IconLock size={14} />}
          {...newAccountForm.getInputProps('password')}
        />
        <TextInput
          placeholder="Account Name"
          leftSection={<IconUser size={14} />}
          {...newAccountForm.getInputProps('name')}
        />
        <div className="flex justify-end space-x-2">
          <Button
            variant="subtle"
            size="xs"
            onClick={() => {
              newAccountForm.reset();
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="xs"
            disabled={mutating}
          >
            {saveButtonText || 'Save Account'}
          </Button>
        </div>
      </form>
    </div>
  );
};
